import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { Asset, Deal } from "@/types/deal";
import { CustomButton } from "@/components/common/customButton";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import {
  buildClosingDisclaimer,
  buildTermSheetHeaderParts,
  buildTermSheetSections,
} from "./buildTermSheetFromDeal";
import { downloadTermSheetDocx } from "./downloadTermSheetDocx";
import { TermSheetPreview } from "./TermSheetPreview";
import { TermSheetSectionList } from "./TermSheetSectionList";
import {
  buildOrderedTermSheetSections,
  createExtraSectionId,
  createInitialRowOrder,
  insertRowAfterParent,
  removeRowFromOrder,
} from "./termSheetHelpers";
import type { TermSheetExtraSection, TermSheetSectionRow } from "./types";

const PAGE_BG = "#EBE8E3";
const PANEL_BG = "#F5F3F0";
const BRAND = "#0E4664";
/** SimpleToolbar height in CreateTermSheet (sticky sibling above this page). */
const PAGE_TOOLBAR_HEIGHT = 56;
const EDITOR_HEADER_HEIGHT = 73;

interface TermSheetEditorProps {
  deal: Deal;
  assets: Asset[];
}

export function TermSheetEditor({ deal, assets }: TermSheetEditorProps) {
  const initialSections = useMemo(
    () => buildTermSheetSections(deal, assets),
    [deal, assets],
  );
  const headerParts = useMemo(
    () => buildTermSheetHeaderParts(deal),
    [deal],
  );

  const [sections, setSections] =
    useState<TermSheetSectionRow[]>(initialSections);
  const [extraSections, setExtraSections] = useState<TermSheetExtraSection[]>(
    [],
  );
  const [rowOrder, setRowOrder] = useState(() =>
    createInitialRowOrder(initialSections),
  );
  const [closingDisclaimer, setClosingDisclaimer] = useState(() =>
    buildClosingDisclaimer(deal),
  );
  const mergedSections = useMemo(
    () => buildOrderedTermSheetSections(sections, extraSections, rowOrder),
    [sections, extraSections, rowOrder],
  );
  const [expandedSectionId, setExpandedSectionId] = useState<string | false>(
    initialSections[0]?.id ?? false,
  );
  const [downloading, setDownloading] = useState(false);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadTermSheetDocx({
        title: headerParts.title,
        date: headerParts.date,
        sections: mergedSections,
        closingDisclaimer,
        dealName: deal.deal_name,
      });
    } catch (error) {
      console.error("Failed to download term sheet:", error);
      showError(
        undefined,
        "The term sheet could not be generated. Please try again.",
        "Download Failed",
      );
    } finally {
      setDownloading(false);
    }
  };

  const updateSectionContent = (sectionId: string, content: string) => {
    setSections((previous) =>
      previous.map((section) =>
        section.id === sectionId ? { ...section, content } : section,
      ),
    );
  };

  const toggleSectionIncluded = (sectionId: string, included: boolean) => {
    setSections((previous) =>
      previous.map((section) =>
        section.id === sectionId ? { ...section, included } : section,
      ),
    );
  };

  const resetSections = () => {
    const nextSections = buildTermSheetSections(deal, assets);
    setSections(nextSections);
    setExtraSections([]);
    setRowOrder(createInitialRowOrder(nextSections));
    setClosingDisclaimer(buildClosingDisclaimer(deal));
  };

  const addExtraSection = (afterSectionId: string) => {
    const newSection: TermSheetExtraSection = {
      id: createExtraSectionId(),
      afterSectionId,
      label: "",
      content: "",
      included: true,
    };
    setExtraSections((previous) => [...previous, newSection]);
    setRowOrder((previous) =>
      insertRowAfterParent(previous, afterSectionId, newSection.id),
    );
  };

  const updateExtraSection = (updated: TermSheetExtraSection) => {
    setExtraSections((previous) =>
      previous.map((section) =>
        section.id === updated.id ? updated : section,
      ),
    );
  };

  const deleteExtraSection = (sectionId: string) => {
    setExtraSections((previous) =>
      previous.filter((section) => section.id !== sectionId),
    );
    setRowOrder((previous) => removeRowFromOrder(previous, sectionId));
  };

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 64px)" }}>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          py: 2,
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E0DDD8",
          position: "sticky",
          top: PAGE_TOOLBAR_HEIGHT,
          zIndex: 1090,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: BRAND,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            TS
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#182737", lineHeight: 1.2 }}>
              Term Sheet Editor
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "0.875rem" }}>
              {deal.deal_name} – Secured Term Loan Facility
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            component="button"
            onClick={resetSections}
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: BRAND,
              cursor: "pointer",
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
            }}
          >
            Reset from deal
          </Typography>
          <CustomButton
            text="Download Word doc"
            handleButtonPress={() => void handleDownload()}
            isButtonDisabled={downloading}
            isLoading={downloading}
            isLoadingText="Downloading"
            solid
          />
        </Box>
      </Box>

      <Grid container>
        <Grid
          size={{ xs: 12, lg: 6 }}
          sx={{
            bgcolor: PANEL_BG,
            px: 3,
            py: 2.5,
            borderRight: { lg: "1px solid #E0DDD8" },
          }}
        >
          <Typography
            sx={{ color: "#6B7280", fontSize: "0.875rem", mb: 2, lineHeight: 1.5 }}
          >
            Edit any section value below. Drag the handle to reorder rows in the
            preview. Use the + button on a section to add a custom row. Sections
            with no deal data start hidden but can be included manually.
          </Typography>

          <TermSheetSectionList
            sections={sections}
            extraSections={extraSections}
            rowOrder={rowOrder}
            onRowOrderChange={setRowOrder}
            expandedSectionId={expandedSectionId}
            onExpandedSectionIdChange={setExpandedSectionId}
            onUpdateSectionContent={updateSectionContent}
            onToggleSectionIncluded={toggleSectionIncluded}
            onAddExtraSection={addExtraSection}
            onUpdateExtraSection={updateExtraSection}
            onDeleteExtraSection={deleteExtraSection}
          />

          <Accordion
            expanded={expandedSectionId === "closingDisclaimer"}
            onChange={(_event, isExpanded) =>
              setExpandedSectionId(isExpanded ? "closingDisclaimer" : false)
            }
            sx={{
              mb: 1.5,
              borderRadius: "10px !important",
              border: "1px solid #E8E5E0",
              bgcolor: "#FFFFFF",
              boxShadow: "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": { mb: 1.5 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#9CA3AF" }} />}
              sx={{
                px: 2,
                minHeight: 48,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  my: 1.25,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: BRAND,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#182737",
                    lineHeight: 1.25,
                  }}
                >
                  Closing disclaimer / signatures
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  color: "#9CA3AF",
                  mb: 1,
                }}
              >
                SECTION TEXT
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={12}
                value={closingDisclaimer}
                onChange={(event) => setClosingDisclaimer(event.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#FAFAF9",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid
          size={{ xs: 12, lg: 6 }}
          sx={{
            bgcolor: PAGE_BG,
            p: 0,
            position: { lg: "sticky" },
            top: { lg: PAGE_TOOLBAR_HEIGHT + EDITOR_HEADER_HEIGHT },
            alignSelf: { lg: "flex-start" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: {
                lg: `calc(100vh - ${PAGE_TOOLBAR_HEIGHT + EDITOR_HEADER_HEIGHT}px)`,
              },
              border: "1px solid #BFBFBF",
              bgcolor: "#FFFFFF",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              borderRadius: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                overflow: { xs: "visible", lg: "auto" },
                flex: 1,
                minHeight: 0,
              }}
            >
              <TermSheetPreview
                title={headerParts.title}
                date={headerParts.date}
                sections={mergedSections}
                closingDisclaimer={closingDisclaimer}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
